import { useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ShoppingCart, AlertCircle, CheckCircle2 } from 'lucide-react';
import {
  UniversalPage,
  UniversalLoadingState,
  Card,
  Button,
  NotificationCard,
  MoneyCell,
  useToast,
} from '@afrilink/ui';
import { domainService } from '@/domains/domains-registry/services/domainService';
import {
  RegistrantForm,
  EMPTY_REGISTRANT,
  trimRegistrant,
  validateRegistrant,
  type RegistrantInput,
} from './components/RegistrantForm';

/**
 * Customer-side registration confirm. Visited from RegisterDomainPage's
 * "Register" button on an available result.
 *
 * 1. Pulls a quote (re-validates availability + price live).
 * 2. Renders the shared registrant form.
 * 3. Submit → domain-register-confirm EF → invoice created → navigate to
 *    /app/billing/invoices/:id where the customer pays. The post-payment
 *    saga handles the registrar mutation automatically.
 */
export default function RegisterDomainConfirmPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { fqdn: encodedFqdn } = useParams();
  const fqdn = encodedFqdn ? decodeURIComponent(encodedFqdn).toLowerCase() : '';

  const [quote, setQuote] = useState<Awaited<ReturnType<typeof domainService.registerQuote>> | null>(null);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(true);

  const [registrant, setRegistrant] = useState<RegistrantInput>(EMPTY_REGISTRANT);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Load quote on mount
  if (quoteLoading && fqdn && !quote && !quoteError) {
    domainService
      .registerQuote(fqdn, 1)
      .then((q) => {
        setQuote(q);
        setQuoteLoading(false);
      })
      .catch((e: unknown) => {
        setQuoteError(e instanceof Error ? e.message : String(e));
        setQuoteLoading(false);
      });
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitError(null);

    const validationError = validateRegistrant(registrant);
    if (validationError) {
      setSubmitError(validationError);
      return;
    }

    setSubmitting(true);
    try {
      // DT-1 (2026-05-11): register flow now routes through a support ticket.
      // The customer is handed to /app/domains/intent/:ticket_id where they
      // see status + can pay the linked invoice. Staff executes the registry
      // mutation within the SLA.
      const r = await domainService.intentTicket({
        operation: 'register',
        payload: {
          domain: fqdn,
          period_years: 1,
          registrant: trimRegistrant(registrant),
        },
      });
      toast({
        tone: 'success',
        title: 'Request received',
        body: r.payment_required && r.total_amount !== undefined
          ? `Invoice ${r.invoice_number} for R${r.total_amount.toFixed(2)} ready — support will action once paid.`
          : 'Support has been notified and will be in touch shortly.',
      });
      navigate(r.redirect_url || `/app/domains/intent/${r.ticket_id}`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <UniversalPage
      title="Confirm registration"
      breadcrumbs={[
        { label: 'Home', href: '/app' },
        { label: 'Domains', href: '/app/domains' },
        { label: 'Register', href: '/app/domains/register' },
        { label: fqdn },
      ]}
    >
      {quoteLoading && (
        <Card interactive={false}>
          <UniversalLoadingState variant="spinner" caption={`Checking ${fqdn}…`} />
        </Card>
      )}

      {quoteError && (
        <NotificationCard
          tone="danger"
          title="Could not get a quote"
          body={
            <>
              {quoteError}
              <div className="mt-3">
                <Button variant="outline" size="sm" onClick={() => navigate('/app/domains/register')}>
                  Back to search
                </Button>
              </div>
            </>
          }
        />
      )}

      {quote && !quote.available && (
        <NotificationCard
          tone="warning"
          title={`${fqdn} is no longer available`}
          body={
            <>
              Someone else may have registered it just now, or the registry has moved it to a
              held state.
              <div className="mt-3">
                <Button variant="outline" size="sm" onClick={() => navigate('/app/domains/register')}>
                  Search a different name
                </Button>
              </div>
            </>
          }
        />
      )}

      {quote && quote.available && (
        <Card interactive={false}>
          <header className="flex items-center justify-between border-b border-border bg-success/5 px-6 py-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5 text-success" />
              <div>
                <p className="text-base font-semibold text-navy-primary tabular-nums">
                  {fqdn}
                </p>
                <p className="text-sm text-success">
                  Available · 1-year registration
                </p>
              </div>
            </div>
            <div className="text-right">
              <MoneyCell value={quote.total_zar} />
              <p className="text-xs text-muted-foreground">/ year (incl. VAT where applicable)</p>
            </div>
          </header>

          <form onSubmit={onSubmit} className="space-y-6 p-6">
            <div>
              <h3 className="mb-1 text-sm font-semibold text-navy-primary">Registrant details</h3>
              <p className="text-xs text-muted-foreground">
                ZACR requires registrant info on every <code>.za</code> registration. These details
                appear in the public WHOIS unless you purchase privacy.
              </p>
            </div>

            <RegistrantForm
              value={registrant}
              onChange={setRegistrant}
              disabled={submitting}
              idPrefix="reg"
            />

            {submitError && (
              <div
                role="alert"
                className="flex items-start gap-3 rounded-md border border-danger/30 bg-danger/5 p-3 text-sm text-danger"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{submitError}</span>
              </div>
            )}

            <div className="flex items-center justify-between border-t border-border pt-4">
              <p className="text-xs text-muted-foreground">
                Submitting creates an invoice. The domain registers automatically once you pay.
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/app/domains/register')}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button type="submit" loading={submitting} disabled={submitting}>
                  {submitting ? null : <ShoppingCart className="size-4" />}
                  {submitting ? 'Creating invoice…' : `Continue to payment · R${quote.total_zar.toFixed(2)}`}
                </Button>
              </div>
            </div>
          </form>
        </Card>
      )}
    </UniversalPage>
  );
}
